import React, { useEffect, useState, useCallback } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../../utils/dateUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { godCompanyApi, godAuditApi, godMetricsApi, godOffboardingApi } from '../../api';
import { GS, G, StatusPill, GField, GSection } from './_godTheme';

// ── Edit Modal ─────────────────────────────────────────────────────────────────
function EditModal({ company, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: company.name || '', plan: company.plan || 'TRIAL',
    subscriptionStart: company.subscriptionStart || '', subscriptionEnd: company.subscriptionEnd || '',
    maxEmployees: company.maxEmployees || 60, maxDepartments: company.maxDepartments || 15,
    notes: company.notes || '',
  });
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const res = await godCompanyApi.update(company.id, form);
      if (res.data.success) { onSaved(res.data.data); onClose(); }
    } catch (e) { setError(e.response?.data?.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  return (
    <div style={GS.overlay}>
      <div style={GS.modal}>
        <div style={GS.modalHdr}>
          <h3 style={GS.modalTitle}>Edit Company</h3>
          <button onClick={onClose} style={GS.modalClose}>×</button>
        </div>
        {error && <div style={GS.errBox}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <GSection label="Company">
            <GField label="Company Name *">
              <input value={form.name} onChange={e => set('name', e.target.value)} style={GS.input}
                onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
            </GField>
            <GField label="Plan">
              <input value={form.plan} onChange={e => set('plan', e.target.value)} placeholder="TRIAL / STARTER / GROWTH / PROFESSIONAL" style={GS.input}
                onFocus={e => e.target.style.borderColor = G.gold} onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
            </GField>
          </GSection>
          <GSection label="Subscription">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <GField label="Start Date"><input type="date" value={form.subscriptionStart} onChange={e => set('subscriptionStart', e.target.value)} style={GS.input} /></GField>
              <GField label="End Date"><input type="date" value={form.subscriptionEnd} onChange={e => set('subscriptionEnd', e.target.value)} style={GS.input} /></GField>
              <GField label="Max Employees"><input type="number" min={1} value={form.maxEmployees} onChange={e => set('maxEmployees', parseInt(e.target.value))} style={GS.input} /></GField>
              <GField label="Max Departments"><input type="number" min={1} value={form.maxDepartments} onChange={e => set('maxDepartments', parseInt(e.target.value))} style={GS.input} /></GField>
            </div>
          </GSection>
          <GSection label="Internal Notes">
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...GS.input, resize: 'vertical' }} />
          </GSection>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={GS.btnSecondary}>Cancel</button>
            <button onClick={submit} disabled={saving} style={GS.btnPrimary}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Suspend Modal ──────────────────────────────────────────────────────────────
function SuspendModal({ company, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const submit = async () => {
    setSaving(true); setError('');
    try { await godCompanyApi.suspend(company.id, reason); onDone(); }
    catch (e) { setError(e.response?.data?.message || 'Failed to suspend'); }
    finally { setSaving(false); }
  };

  return (
    <div style={GS.overlay}>
      <div style={{ ...GS.modal, maxWidth: '440px' }}>
        <div style={GS.modalHdr}>
          <h3 style={{ ...GS.modalTitle, color: '#DC2626' }}>Suspend Company</h3>
          <button onClick={onClose} style={GS.modalClose}>×</button>
        </div>
        <p style={{ fontSize: '13px', color: G.textSub, marginBottom: '14px', lineHeight: 1.6 }}>
          Suspending <strong>{company.name}</strong> will immediately block all tenant users from logging in.
          Their data is preserved and you can reactivate at any time.
        </p>
        {error && <div style={GS.errBox}>{error}</div>}
        <GField label="Reason (recorded in audit log)">
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
            placeholder="e.g. Payment overdue, subscription expired…"
            style={{ ...GS.input, resize: 'vertical', marginTop: '6px' }} />
        </GField>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onClose} style={GS.btnSecondary}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...GS.btnDanger, background: '#EF4444', color: '#fff' }}>
            {saving ? 'Suspending…' : 'Confirm Suspend'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SA Password Reset Modal ────────────────────────────────────────────────────
function ResetSAModal({ company, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const submit = async () => {
    setSaving(true); setError('');
    try { await godCompanyApi.resetSuperAdmin(company.id); onDone(); }
    catch (e) { setError(e.response?.data?.message || 'Failed to reset password'); }
    finally { setSaving(false); }
  };

  return (
    <div style={GS.overlay}>
      <div style={{ ...GS.modal, maxWidth: '440px' }}>
        <div style={GS.modalHdr}>
          <h3 style={{ ...GS.modalTitle, color: G.gold }}>Reset Super Admin Password</h3>
          <button onClick={onClose} style={GS.modalClose}>×</button>
        </div>
        <div style={{ padding: '12px 14px', background: G.goldLight, border: `1px solid ${G.goldBorder}`, borderRadius: '10px', marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', color: G.goldDeep, lineHeight: 1.6 }}>
            This will reset the Super Admin password for <strong>{company.name}</strong> to the
            system default (<code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: '4px' }}>Tracern@123</code>).
            The Super Admin will be forced to change it on their next login.
            This action is permanently logged in the God Audit Log.
          </p>
        </div>
        <p style={{ fontSize: '13px', color: G.textMuted, marginBottom: '16px' }}>
          Super Admin Employee ID: <strong style={{ color: G.text }}>{company.superAdminEmployeeId}</strong>
        </p>
        {error && <div style={GS.errBox}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={GS.btnSecondary}>Cancel</button>
          <button onClick={submit} disabled={saving} style={GS.btnPrimary}>
            {saving ? 'Resetting…' : '🔐 Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MetaRow ────────────────────────────────────────────────────────────────────
function MetaRow({ label, value, valueColor, badge }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${G.border}` }}>
      <span style={{ fontSize: '13px', color: G.textMuted }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 600, color: valueColor || G.text }}>
        {badge ? <StatusPill status={value} /> : (value || '—')}
      </span>
    </div>
  );
}

// ── Mini stat tile ─────────────────────────────────────────────────────────────
function MiniTile({ label, value, color }) {
  return (
    <div style={{ background: G.bg, borderRadius: '10px', padding: '12px 14px', textAlign: 'center', border: `1px solid ${G.border}` }}>
      <p style={{ fontSize: '22px', fontWeight: 800, color: color || G.gold, letterSpacing: '-0.04em', lineHeight: 1 }}>{value ?? 0}</p>
      <p style={{ fontSize: '11px', color: G.textMuted, marginTop: '4px', fontWeight: 500 }}>{label}</p>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function GodCompanyDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [company,   setCompany]   = useState(null);
  const [metrics,   setMetrics]   = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [showEdit,    setShowEdit]    = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showResetSA, setShowResetSA] = useState(false);
  const [resettingMfa, setResettingMfa] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      godCompanyApi.getById(id),
      godAuditApi.getByCompany(id, { page: 0, size: 8 }),
      godMetricsApi.companyMetrics(id),
    ])
      .then(([c, l, m]) => {
        setCompany(c.data.data);
        setAuditLogs(l.data.data.items || []);
        setMetrics(m.data.data);
      })
      .catch(() => setError('Failed to load company'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleReactivate = async () => {
    setError(''); setSuccess('');
    try {
      const res = await godCompanyApi.reactivate(id);
      setCompany(res.data.data);
      setSuccess('Company reactivated successfully');
    } catch (e) { setError(e.response?.data?.message || 'Failed to reactivate'); }
  };

  const handleExport = async () => {
    setError(''); setSuccess('');
    try {
      const res = await godOffboardingApi.exportCompany(id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `tracern-export-${company?.slug}.zip`; a.click();
      URL.revokeObjectURL(url);
      setSuccess('Data export downloaded');
    } catch (e) { setError(e.response?.data?.message || 'Export failed'); }
  };

  const handleResetSaMfa = async () => {
    if (!window.confirm(`Reset MFA for Super Admin of ${company?.name}? They will need to set up 2FA again on next login.`)) return;
    setResettingMfa(true); setError(''); setSuccess('');
    try {
      await godCompanyApi.resetSaMfa(company.companyId, company.superAdminEmployeeId);
      setSuccess('Super Admin MFA has been reset. They will need to reconfigure 2FA on next login.');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to reset MFA');
    } finally {
      setResettingMfa(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: G.textMuted }}>Loading…</div>;
  if (error && !company) return <div style={{ padding: '24px', color: '#DC2626' }}>{error}</div>;
  if (!company) return null;

  const isSuspended = company.status === 'SUSPENDED';
  const isChurned   = company.status === 'CHURNED';

  return (
    <div style={{ maxWidth: '860px' }}>
      {/* Back */}
      <button onClick={() => navigate('/god/companies')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: G.textMuted, fontSize: '14px', padding: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
        ← All Companies
      </button>

      {error   && <div style={GS.errBox}>{error}</div>}
      {success && <div style={GS.sucBox}>{success}</div>}

      {/* Header card */}
      <div style={{ ...GS.card, marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: G.goldLight, color: G.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 800, flexShrink: 0, fontFamily: G.fontBrand }}>
              {company.name?.[0]?.toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                <h1 style={{ fontSize: '20px', fontWeight: 800, color: G.text, letterSpacing: '-0.04em', fontFamily: G.fontBrand }}>{company.name}</h1>
                <StatusPill status={company.status} />
              </div>
              <p style={{ fontSize: '13px', color: G.textMuted }}>{company.slug} · {company.companyId}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flexShrink: 0 }}>
            <button onClick={() => setShowEdit(true)} style={{ ...actionBtn, color: G.textSub, borderColor: G.border }}>✏️ Edit</button>
            <button onClick={() => setShowResetSA(true)} style={{ ...actionBtn, color: G.gold, borderColor: G.goldBorder, background: G.goldLight }}>🔐 Reset SA Pwd</button>
            <button onClick={handleResetSaMfa} disabled={resettingMfa} style={{ ...actionBtn, color: G.gold, borderColor: G.goldBorder, background: G.goldLight }}>
              {resettingMfa ? '…' : '📱 Reset SA MFA'}
            </button>
            <button onClick={handleExport} style={{ ...actionBtn, color: '#1D4ED8', borderColor: '#BFDBFE', background: '#EFF6FF' }}>📦 Export</button>
            {isChurned
              ? <button onClick={() => navigate('/god/offboarding')} style={{ ...actionBtn, color: '#DC2626', borderColor: '#FECACA', background: '#FEF2F2' }}>🗑 Offboard</button>
              : isSuspended
                ? <button onClick={handleReactivate} style={{ ...actionBtn, color: '#16A34A', borderColor: '#BBF7D0', background: '#F0FDF4' }}>✅ Reactivate</button>
                : <button onClick={() => setShowSuspend(true)} style={{ ...actionBtn, color: '#DC2626', borderColor: '#FECACA', background: '#FEF2F2' }}>🚫 Suspend</button>
            }
          </div>
        </div>
      </div>

      {/* Two column */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '14px' }}>
        {/* Metrics grid */}
        <div style={GS.card}>
          <p style={GS.cardTitle}>Company Metrics</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '2px' }}>
            <MiniTile label="Employees"   value={company.employeeCount}   color="#6366F1" />
            <MiniTile label="Active Tasks" value={company.activeTaskCount} color="#16A34A" />
            <MiniTile label="Max Empl."   value={company.maxEmployees}    color={G.textMuted} />
            <MiniTile label="Max Depts."  value={company.maxDepartments}  color={G.textMuted} />
          </div>
        </div>

        {/* Subscription details */}
        <div style={GS.card}>
          <p style={GS.cardTitle}>Subscription</p>
          <MetaRow label="Status"        value={company.status}  badge />
          <MetaRow label="Plan"          value={company.plan} />
          <MetaRow label="Start"         value={company.subscriptionStart} />
          <MetaRow label="End"           value={company.subscriptionEnd}
            valueColor={company.daysUntilExpiry <= 14 ? '#D97706' : company.daysUntilExpiry < 0 ? '#DC2626' : undefined} />
          <MetaRow label="Days remaining" value={
            company.subscriptionEnd
              ? (company.daysUntilExpiry >= 0 ? `${company.daysUntilExpiry} days` : `Expired ${Math.abs(company.daysUntilExpiry)}d ago`)
              : '—'
          } valueColor={company.daysUntilExpiry < 0 ? '#DC2626' : company.daysUntilExpiry <= 14 ? '#D97706' : '#16A34A'} />
          <MetaRow label="Super Admin ID" value={company.superAdminEmployeeId} />
          <MetaRow label="Created"        value={company.createdAt ? fmtDate(company.createdAt) : '—'} />
        </div>
      </div>

      {/* Tenant health */}
      {metrics && (
        <div style={{ ...GS.card, marginBottom: '14px' }}>
          <p style={GS.cardTitle}>Tenant Health</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px', marginTop: '8px' }}>
            {[
              { label: 'Total Tasks',  value: metrics.totalTasks,     color: '#6366F1' },
              { label: 'Done',         value: metrics.doneTasks,      color: '#16A34A' },
              { label: 'Active',       value: metrics.activeTasks,    color: '#2563EB' },
              { label: 'Overdue',      value: metrics.overdueTasks,   color: metrics.overdueTasks > 0 ? '#DC2626' : G.textMuted },
              { label: 'This Month',   value: metrics.tasksThisMonth, color: G.gold    },
              { label: 'Completion',   value: `${metrics.completionRate || 0}%`, color: '#16A34A' },
            ].map(m => <MiniTile key={m.label} {...m} />)}
          </div>

          {/* Usage bar */}
          {metrics.employeeUsagePct !== undefined && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: G.textMuted }}>Employee usage</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: metrics.employeeUsagePct > 90 ? '#DC2626' : G.textSub }}>
                  {metrics.employeeCount}/{metrics.maxEmployees} ({metrics.employeeUsagePct}%)
                </span>
              </div>
              <div style={{ height: '6px', background: G.bg, borderRadius: '999px', overflow: 'hidden', border: `1px solid ${G.border}` }}>
                <div style={{
                  height: '100%', borderRadius: '999px', transition: 'width 0.4s',
                  width: `${Math.min(metrics.employeeUsagePct, 100)}%`,
                  background: metrics.employeeUsagePct > 90 ? '#EF4444' : metrics.employeeUsagePct > 75 ? '#F59E0B' : '#16A34A',
                }} />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '12px' }}>
            <span style={{ fontSize: '12px', color: G.textMuted }}>
              Last SA login: <strong style={{ color: G.text }}>{metrics.lastSALogin ? fmtDate(metrics.lastSALogin) : 'Never'}</strong>
            </span>
            <span style={{ fontSize: '12px', color: G.textMuted }}>
              Storage: <strong style={{ color: G.text }}>{metrics.storageEstimateKB < 1024 ? `${metrics.storageEstimateKB} KB` : `${(metrics.storageEstimateKB / 1024).toFixed(1)} MB`}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Internal notes */}
      {company.notes && (
        <div style={{ ...GS.card, marginBottom: '14px' }}>
          <p style={GS.cardTitle}>Internal Notes</p>
          <p style={{ fontSize: '13px', color: G.textSub, lineHeight: 1.7, marginTop: '8px', whiteSpace: 'pre-wrap' }}>{company.notes}</p>
        </div>
      )}

      {/* Recent God audit log for this company */}
      <div style={GS.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <p style={GS.cardTitle}>Recent God Mode Activity</p>
          <button onClick={() => navigate('/god/audit-log')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: G.gold, fontWeight: 600 }}>
            View full log →
          </button>
        </div>
        {!auditLogs.length
          ? <p style={{ fontSize: '13px', color: G.textMuted }}>No God Mode actions recorded yet for this company.</p>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {auditLogs.map(log => (
                <div key={log.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 12px', background: G.bg, borderRadius: '8px', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: G.gold }}>{log.performedBy}</span>
                    <span style={{ fontSize: '12px', color: G.textMuted }}> — {log.action?.replace(/_/g, ' ')}</span>
                    {log.details && <p style={{ fontSize: '11px', color: G.textFaint, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.details.slice(0, 90)}{log.details.length > 90 ? '…' : ''}</p>}
                  </div>
                  <span style={{ fontSize: '11px', color: G.textFaint, flexShrink: 0 }}>{timeAgo(log.timestamp)}</span>
                </div>
              ))}
            </div>
        }
      </div>

      {/* Modals */}
      {showEdit && (
        <EditModal company={company} onClose={() => setShowEdit(false)}
          onSaved={updated => { setCompany(updated); setShowEdit(false); setSuccess('Company updated'); }} />
      )}
      {showSuspend && (
        <SuspendModal company={company} onClose={() => setShowSuspend(false)}
          onDone={() => { setShowSuspend(false); setSuccess('Company suspended'); load(); }} />
      )}
      {showResetSA && (
        <ResetSAModal company={company} onClose={() => setShowResetSA(false)}
          onDone={() => { setShowResetSA(false); setSuccess('Super Admin password reset to default. Tenant must change on next login.'); }} />
      )}
    </div>
  );
}

const actionBtn = {
  padding: '7px 12px', borderRadius: '8px', cursor: 'pointer',
  fontSize: '12px', fontWeight: 600, border: '1px solid',
  background: 'transparent', transition: 'opacity 0.12s',
};
